export type MazeCell = { x: number; y: number };
export type RouteGateRoom = { left: number; top: number; right: number; bottom: number };
export type RouteGate = { choiceIndex: number; cell: MazeCell; room: RouteGateRoom };
export type RoutePortal = { id: string; pairId: string; cell: MazeCell };

export type RouteMaze = {
  width: number;
  height: number;
  /** 0 = yürünebilir koridor ya da cevap avlusu, 1 = bahçe çiti. */
  tiles: number[][];
  playerSpawn: MazeCell;
  enemySpawns: MazeCell[];
  gates: RouteGate[];
  portals: RoutePortal[];
  /** Cevap kapıları koridorun üzerinde değil, erişilebilir küçük avlulardadır. */
  gateRoomCells: MazeCell[];
};

const WIDTH = 32;
const HEIGHT = 18;
const PATH = 0;
const WALL = 1;
const CARDINALS = [[1, 0], [-1, 0], [0, 1], [0, -1]] as const;

type Room = RouteGateRoom & { gate: MazeCell };
type RouteBlueprint = { id: string; segments: Array<readonly [number, number, number, number]>; rooms: Room[] };

function key(cell: MazeCell): string { return `${cell.x},${cell.y}`; }
function point(x: number, y: number): MazeCell { return { x, y }; }
function blankMaze(): number[][] { return Array.from({ length: HEIGHT }, () => Array<number>(WIDTH).fill(WALL)); }

function carveLine(tiles: number[][], fromX: number, fromY: number, toX: number, toY: number) {
  if (fromX === toX) {
    for (let y = Math.min(fromY, toY); y <= Math.max(fromY, toY); y += 1) tiles[y][fromX] = PATH;
    return;
  }
  if (fromY === toY) {
    for (let x = Math.min(fromX, toX); x <= Math.max(fromX, toX); x += 1) tiles[fromY][x] = PATH;
    return;
  }
  throw new Error('Alevli Rota koridorları yalnız yatay veya dikey olabilir.');
}

function carveRoom(tiles: number[][], room: Room): MazeCell[] {
  const cells: MazeCell[] = [];
  for (let y = room.top; y <= room.bottom; y += 1) {
    for (let x = room.left; x <= room.right; x += 1) {
      tiles[y][x] = PATH;
      cells.push(point(x, y));
    }
  }
  return cells;
}

/**
 * Bunlar rastgele üretilmiş labirentler değildir. Her seçenek sayısı için
 * ayrı, önceden çizilmiş Pac-Man tarzı bir rota tahtası vardır. Ortak dil:
 * merkez kavşak, dış halka, kapı avluları ve yalnız yatay dış tüneller.
 */
const BLUEPRINTS: Record<2 | 3 | 4 | 5 | 6, RouteBlueprint> = {
  2: {
    id: 'iki-kapili-dikey-halkalar',
    segments: [
      [1, 1, 30, 1], [1, 16, 30, 16], [1, 1, 1, 16], [30, 1, 30, 16],
      [0, 8, 31, 8], [16, 1, 16, 16],
      [10, 4, 22, 4], [10, 13, 22, 13], [10, 4, 10, 13], [22, 4, 22, 13],
      [13, 6, 19, 6], [13, 11, 19, 11], [13, 6, 13, 11], [19, 6, 19, 11],
    ],
    rooms: [
      { left: 13, top: 2, right: 19, bottom: 4, gate: point(16, 3) },
      { left: 13, top: 12, right: 19, bottom: 14, gate: point(16, 13) },
    ],
  },
  3: {
    id: 'uc-kapili-ucgen-halkalar',
    segments: [
      [1, 1, 30, 1], [1, 16, 30, 16], [1, 1, 1, 16], [30, 1, 30, 16],
      [0, 8, 31, 8], [16, 1, 16, 16],
      [6, 4, 26, 4], [6, 13, 26, 13], [6, 1, 6, 16], [26, 1, 26, 16],
      [11, 6, 21, 6], [11, 11, 21, 11], [11, 6, 11, 11], [21, 6, 21, 11],
    ],
    rooms: [
      { left: 3, top: 2, right: 7, bottom: 4, gate: point(5, 3) },
      { left: 24, top: 2, right: 28, bottom: 4, gate: point(26, 3) },
      { left: 13, top: 12, right: 19, bottom: 14, gate: point(16, 13) },
    ],
  },
  4: {
    id: 'dort-kapili-merkez-pusula',
    segments: [
      [1, 1, 30, 1], [1, 16, 30, 16], [1, 1, 1, 16], [30, 1, 30, 16],
      [0, 8, 31, 8], [16, 1, 16, 16],
      [6, 4, 26, 4], [6, 13, 26, 13], [6, 1, 6, 16], [26, 1, 26, 16],
      [12, 6, 20, 6], [12, 11, 20, 11], [12, 6, 12, 11], [20, 6, 20, 11],
    ],
    rooms: [
      { left: 13, top: 2, right: 19, bottom: 4, gate: point(16, 3) },
      { left: 3, top: 7, right: 7, bottom: 9, gate: point(5, 8) },
      { left: 25, top: 7, right: 29, bottom: 9, gate: point(27, 8) },
      { left: 13, top: 12, right: 19, bottom: 14, gate: point(16, 13) },
    ],
  },
  5: {
    id: 'bes-kapili-besli-bahce',
    segments: [
      [1, 1, 30, 1], [1, 16, 30, 16], [1, 1, 1, 16], [30, 1, 30, 16],
      [0, 8, 31, 8], [16, 1, 16, 16],
      [6, 4, 26, 4], [6, 13, 26, 13], [6, 1, 6, 16], [26, 1, 26, 16],
      [10, 6, 22, 6], [10, 11, 22, 11], [10, 6, 10, 11], [22, 6, 22, 11],
    ],
    rooms: [
      { left: 3, top: 2, right: 7, bottom: 4, gate: point(5, 3) },
      { left: 24, top: 2, right: 28, bottom: 4, gate: point(26, 3) },
      { left: 3, top: 7, right: 7, bottom: 9, gate: point(5, 8) },
      { left: 25, top: 7, right: 29, bottom: 9, gate: point(27, 8) },
      { left: 13, top: 12, right: 19, bottom: 14, gate: point(16, 13) },
    ],
  },
  6: {
    id: 'alti-kapili-cifte-halkalar',
    segments: [
      [1, 1, 30, 1], [1, 16, 30, 16], [1, 1, 1, 16], [30, 1, 30, 16],
      [0, 8, 31, 8], [16, 1, 16, 16],
      [6, 4, 26, 4], [6, 13, 26, 13], [6, 1, 6, 16], [26, 1, 26, 16],
      [10, 6, 22, 6], [10, 11, 22, 11], [10, 6, 10, 11], [22, 6, 22, 11],
    ],
    rooms: [
      { left: 3, top: 2, right: 7, bottom: 4, gate: point(5, 3) },
      { left: 24, top: 2, right: 28, bottom: 4, gate: point(26, 3) },
      { left: 3, top: 7, right: 7, bottom: 9, gate: point(5, 8) },
      { left: 25, top: 7, right: 29, bottom: 9, gate: point(27, 8) },
      { left: 3, top: 12, right: 7, bottom: 14, gate: point(5, 13) },
      { left: 24, top: 12, right: 28, bottom: 14, gate: point(26, 13) },
    ],
  },
};

function blueprintFor(choiceCount: number): RouteBlueprint {
  return BLUEPRINTS[Math.max(2, Math.min(6, Math.trunc(choiceCount))) as 2 | 3 | 4 | 5 | 6];
}

function buildBoard(blueprint: RouteBlueprint): { tiles: number[][]; gateRoomCells: MazeCell[] } {
  const tiles = blankMaze();
  blueprint.segments.forEach(([fromX, fromY, toX, toY]) => carveLine(tiles, fromX, fromY, toX, toY));
  const gateRoomCells = blueprint.rooms.flatMap((room) => carveRoom(tiles, room));
  return { tiles, gateRoomCells };
}

function portalsFor(): RoutePortal[] {
  return [
    { id: 'sol-tunel', pairId: 'sag-tunel', cell: point(0, 8) },
    { id: 'sag-tunel', pairId: 'sol-tunel', cell: point(31, 8) },
  ];
}

function portalExitMap(maze: RouteMaze): Map<string, MazeCell> {
  const byId = new Map(maze.portals.map((portal) => [portal.id, portal]));
  return new Map(maze.portals.map((portal) => [key(portal.cell), byId.get(portal.pairId)?.cell ?? portal.cell]));
}

/** Grid üzerinde portal tünellerini de hesaba katan gerçek rota uzaklıkları. */
export function routeDistancesFrom(maze: RouteMaze, start: MazeCell): Map<string, number> {
  const distances = new Map<string, number>([[key(start), 0]]);
  const queue = [start];
  const exits = portalExitMap(maze);
  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    const currentDistance = distances.get(key(current)) ?? 0;
    const neighbours = CARDINALS
      .map(([dx, dy]) => point(current.x + dx, current.y + dy))
      .filter((next) => maze.tiles[next.y]?.[next.x] === PATH);
    const portalExit = exits.get(key(current));
    if (portalExit) neighbours.push(portalExit);
    neighbours.forEach((next) => {
      const nextKey = key(next);
      if (distances.has(nextKey)) return;
      distances.set(nextKey, currentDistance + 1);
      queue.push(next);
    });
  }
  return distances;
}

function selectDistantEnemySpawns(maze: RouteMaze, count: number, gateRoomCells: MazeCell[]): MazeCell[] {
  const distances = routeDistancesFrom(maze, maze.playerSpawn);
  const forbidden = new Set([...gateRoomCells, ...maze.portals.map((portal) => portal.cell)].map(key));
  const candidates: Array<{ cell: MazeCell; distance: number }> = [];
  for (let y = 0; y < maze.height; y += 1) {
    for (let x = 0; x < maze.width; x += 1) {
      const cell = point(x, y);
      const distance = distances.get(key(cell));
      if (maze.tiles[y][x] !== PATH || distance === undefined || forbidden.has(key(cell))) continue;
      const degree = CARDINALS.filter(([dx, dy]) => maze.tiles[y + dy]?.[x + dx] === PATH).length;
      if (degree < 2 || distance < 14) continue;
      candidates.push({ cell, distance });
    }
  }
  candidates.sort((a, b) => b.distance - a.distance || a.cell.y - b.cell.y || a.cell.x - b.cell.x);
  const selected: MazeCell[] = [];
  while (candidates.length > 0 && selected.length < count) {
    const next = candidates.shift();
    if (!next) break;
    // Aynı köşeye yığılmasınlar: her Çise farklı, uzak bir rota kolundan doğar.
    if (selected.some((other) => Math.abs(other.x - next.cell.x) + Math.abs(other.y - next.cell.y) < 10)) continue;
    selected.push(next.cell);
  }
  return selected;
}

/** `seed` yayın sözleşmesi için korunur; tahta geometrisi özellikle sabittir. */
export function generateRouteMaze(_seed: string, choiceCount: number): RouteMaze {
  const safeChoices = Math.max(2, Math.min(6, Math.trunc(choiceCount)));
  const blueprint = blueprintFor(safeChoices);
  const { tiles, gateRoomCells } = buildBoard(blueprint);
  const maze: RouteMaze = {
    width: WIDTH,
    height: HEIGHT,
    tiles,
    playerSpawn: point(16, 8),
    enemySpawns: [],
    gates: blueprint.rooms.map((room, choiceIndex) => ({
      choiceIndex,
      cell: room.gate,
      room: { left: room.left, top: room.top, right: room.right, bottom: room.bottom },
    })),
    portals: portalsFor(),
    gateRoomCells,
  };
  // Seviye 9–10'da dört Çise gerekir; sahne gereken sayıyı bu uzak
  // doğuş noktalarından seçer. Erken seviyelerde yalnız ilk 2/3'ü kullanılır.
  maze.enemySpawns = selectDistantEnemySpawns(maze, 4, gateRoomCells);
  return maze;
}

/**
 * Oyuncu bir cevap alanının içinde ama seçimin yapıldığı merkez hücrede değilse o geçidi döndürür.
 * Sahne bu durumda merkezde "buraya gir" işaretini gösterir; alanın içinde dolaşmak seçim sayılmaz.
 */
export function gateRoomAround(maze: Pick<RouteMaze, 'gates'>, cell: MazeCell): RouteGate | undefined {
  return maze.gates.find(({ room, cell: center }) => cell.x >= room.left && cell.x <= room.right && cell.y >= room.top && cell.y <= room.bottom
    && !(cell.x === center.x && cell.y === center.y));
}

export function reachableCellsFrom(maze: RouteMaze, start: MazeCell): Set<string> {
  return new Set(routeDistancesFrom(maze, start).keys());
}

export function deadEndCells(maze: RouteMaze): MazeCell[] {
  const portalCells = new Set(maze.portals.map((portal) => key(portal.cell)));
  const portalExits = portalExitMap(maze);
  const deadEnds: MazeCell[] = [];
  for (let y = 0; y < maze.height; y += 1) {
    for (let x = 0; x < maze.width; x += 1) {
      if (maze.tiles[y][x] !== PATH) continue;
      const cell = point(x, y);
      const routeExits = CARDINALS.filter(([dx, dy]) => maze.tiles[y + dy]?.[x + dx] === PATH).length;
      const tunnelExit = portalCells.has(key(cell)) && portalExits.has(key(cell)) ? 1 : 0;
      if (routeExits + tunnelExit < 2) deadEnds.push(cell);
    }
  }
  return deadEnds;
}
